import { motion } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PROFILE_IMG } from "@/data/content";

const colorMap = {
  blue: { text: "text-blue-600", ring: "border-blue-300 dark:border-blue-700/60" },
  cyan: { text: "text-cyan-500", ring: "border-cyan-300 dark:border-cyan-700/60" },
  gold: { text: "text-yellow-600", ring: "border-yellow-300 dark:border-yellow-700/60" },
  red: { text: "text-red-600", ring: "border-red-300 dark:border-red-700/60" },
  green: { text: "text-emerald-600", ring: "border-emerald-300 dark:border-emerald-700/60" },
};

export const Hero = () => {
  const { t } = useLang();

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden"
    >
      <div
        className="absolute inset-0 -z-10 opacity-[0.05] bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d)",
        }}
      />
      <div className="absolute -top-10 end-1/4 -z-10 h-80 w-80 rounded-full bg-cyan-200/40 dark:bg-cyan-500/10 blur-3xl" />
      <div className="absolute top-40 start-10 -z-10 h-72 w-72 rounded-full bg-yellow-100/50 dark:bg-blue-500/10 blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col items-center text-center">
          {/* Photo — compact circular */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="relative"
          >
            <span className="absolute -inset-2 rounded-full bg-gradient-to-tr from-cyan-400 via-blue-500 to-yellow-400 blur-[2px] opacity-80" />
            <div className="relative h-32 w-32 sm:h-40 sm:w-40 rounded-full p-1 bg-white dark:bg-slate-950">
              <img
                data-testid="hero-portrait"
                src={PROFILE_IMG}
                alt={t.hero.name}
                className="h-full w-full rounded-full object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-6 flex flex-wrap items-center justify-center gap-2"
          >
            <span
              data-testid="verified-badge"
              className="inline-flex items-center gap-2 rounded-full border border-blue-200 dark:border-blue-900/60 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-bold text-slate-900 dark:text-slate-100 shadow-sm ring-1 ring-blue-100 dark:ring-blue-900/40"
            >
              <BadgeCheck className="h-5 w-5 fill-blue-500 text-white" />
              {t.hero.badge}
            </span>
            <span
              data-testid="certified-badge"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 dark:border-emerald-900/60 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-bold text-slate-900 dark:text-slate-100 shadow-sm ring-1 ring-emerald-100 dark:ring-emerald-900/40"
            >
              <BadgeCheck className="h-5 w-5 fill-emerald-500 text-white" />
              {t.hero.certified}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight text-slate-950 dark:text-white"
          >
            {t.hero.name}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 dark:text-slate-400"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            data-testid="hero-tags"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.36 }}
            className="mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 max-w-2xl text-xs sm:text-sm font-medium"
          >
            {t.hero.tags.map((tag, i) => (
              <span
                key={i}
                className="inline-flex items-center text-cyan-600 dark:text-cyan-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <span className="text-cyan-400/70 dark:text-cyan-500/70 me-0.5">#</span>
                {tag}
              </span>
            ))}
          </motion.div>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 w-full max-w-3xl">
            {t.hero.stats.map((s, i) => {
              const c = colorMap[s.color];
              return (
                <motion.div
                  key={i}
                  data-testid={`hero-stat-${i}`}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.12 }}
                  className={`rounded-xl bg-white dark:bg-slate-900 shadow-sm dark:shadow-black/20 px-3 py-5 border ${c.ring}`}
                >
                  <div
                    className={`font-heading text-2xl sm:text-4xl font-black ${c.text}`}
                  >
                    {s.value}
                  </div>
                  <div className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mt-1.5">
                    {s.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
